import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { GenericProducts } from "../../../constants";
import { Banner, axioKit } from "../../../services/utilities";
import { PURCHASE_URL } from "../../../pages/platforms/laboratory/purchaseOrders/common";
import { SCOPES } from "../../../pages/platforms/manager/purchases/inventory/common";

const COLUMNS = [
  ["beg", "Beg."],
  ["add", "Add"],
  ["total", "Total"],
  ["used", "Used"],
  ["expired", "Expired"],
  ["ending", "Ending"],
];

/**
 * Monthly inventory of the branch for the DOH hard copy, in base units:
 * beginning, added, total, used, expired and ending per brand + variant.
 *   /printout/inventory?month=YYYY-MM&scope=reagents|supplies
 */
export default function InventoryPrintout() {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    [records, setRecords] = useState([]),
    [isLoaded, setIsLoaded] = useState(false),
    [error, setError] = useState("");

  const params = new URLSearchParams(window.location.search),
    month = params.get("month") || "",
    kind = SCOPES[params.get("scope")];

  const branch = activePlatform?.branchId,
    company =
      activePlatform?.branch?.companyId?.name ||
      activePlatform?.company?.name ||
      "",
    branchName = activePlatform?.branch?.name || "";

  useEffect(() => {
    if (!branch || !token || !month) return;
    axioKit
      .universal(`${PURCHASE_URL}/monthly`, token, { branch, month })
      .then(({ payload }) => setRecords(payload || []))
      .catch(({ message }) => setError(message))
      .finally(() => setIsLoaded(true));
  }, [branch, token, month]);

  const rows = useMemo(
    () =>
      records
        .filter(({ pgid }) => GenericProducts.find(pgid))
        .filter(({ pgid }) => !kind || kind.includes(pgid))
        .map((record) => {
          const generic = GenericProducts.find(record.pgid);
          return {
            ...record,
            generic,
            name: [record.product?.name, record.variantName]
              .filter(Boolean)
              .join(" · "),
            base: generic.uom === "test" ? "tests" : "pcs",
          };
        })
        .sort(
          (a, b) =>
            a.generic.name.localeCompare(b.generic.name) ||
            a.name.localeCompare(b.name),
        ),
    [records, kind],
  );

  useEffect(() => {
    if (!isLoaded || error) return;
    const timer = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timer);
  }, [isLoaded, error]);

  const [y, m] = month.split("-").map(Number);
  const monthLabel =
    y && m
      ? new Date(y, m - 1, 1).toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : "";

  return (
    <div className="inventory-printout">
      <style>{`
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        .inventory-printout {
          padding: 10px;
          background: #fff;
          color: #000;
          font-family: Arial, sans-serif;
          font-size: 12px;
        }
        .inventory-printout table {
          width: 100%;
          border-collapse: collapse;
        }
        .inventory-printout th,
        .inventory-printout td {
          border: 1px solid #000;
          padding: 4px 6px;
        }
        .inventory-printout thead {
          display: table-header-group;
        }
        .inventory-printout tr {
          page-break-inside: avoid;
        }
        .inventory-printout .number {
          width: 9%;
          text-align: right;
        }
        .inventory-printout .signatories {
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
          page-break-inside: avoid;
        }
        .inventory-printout .signatories div {
          width: 40%;
          border-top: 1px solid #000;
          padding-top: 4px;
          text-align: center;
        }
      `}</style>

      <Banner company={company} branch={branchName} />

      <h5 className="text-center font-weight-bold mt-3 mb-0">
        MONTHLY {(kind?.title || "Product Inventory").toUpperCase()}
      </h5>
      <p className="text-center mb-3">{monthLabel}</p>

      {error ? (
        <p className="text-center text-danger">{error}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th style={{ width: "4%" }}>#</th>
              <th>Item</th>
              <th style={{ width: "8%" }}>Unit</th>
              {COLUMNS.map(([key, label]) => (
                <th key={key} className="number">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr>
                <td colSpan={COLUMNS.length + 3} className="text-center">
                  {isLoaded ? "No record." : "Loading..."}
                </td>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr key={`${row.product?._id}:${row.variant || ""}`}>
                <td className="text-center">{index + 1}</td>
                <td>
                  <b>{row.generic.name}</b>
                  <div>{row.name}</div>
                </td>
                <td className="text-center">{row.base}</td>
                {COLUMNS.map(([key]) => (
                  <td key={key} className="number">
                    {row[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="signatories">
        <div>Prepared by</div>
        <div>Noted by</div>
      </div>
    </div>
  );
}
